import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  FaGraduationCap, 
  FaTimes, 
  FaArrowRight,
  FaStar,
  FaTrophy,
  FaRocket
} from 'react-icons/fa';

const FloatingDegreeIcon = () => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showCount, setShowCount] = useState(0);
  const [hasShownOnThisPage, setHasShownOnThisPage] = useState(false);
  const [isFlashing, setIsFlashing] = useState(false);

  useEffect(() => {
    // Reset show count when page changes
    setShowCount(0);
    setHasShownOnThisPage(false);
    setIsVisible(false);
    setIsClosing(false);
  }, [location.pathname]);

  useEffect(() => {
    // Show icon after 5 seconds if not shown yet on this page
    if (!hasShownOnThisPage && showCount < 2) {
      const showTimer = setTimeout(() => {
        setIsVisible(true);
        setShowCount(prev => prev + 1);
        setHasShownOnThisPage(true);
      }, 5000);

      return () => clearTimeout(showTimer);
    }
  }, [hasShownOnThisPage, showCount]);

  useEffect(() => {
    // Flashing animation
    if (isVisible) {
      const flashInterval = setInterval(() => {
        setIsFlashing(prev => !prev);
      }, 1000);
      return () => clearInterval(flashInterval);
    }
  }, [isVisible]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      // Show second time after 15 seconds if not shown twice yet
      if (showCount < 2) {
        setTimeout(() => {
          setIsVisible(true);
          setIsClosing(false);
          setShowCount(prev => prev + 1);
        }, 15000);
      }
    }, 300);
  };

  const handleClick = () => {
    // Redirect to EXEED website
    window.open('http://www.myexeed.com/', '_blank');
  };

  if (!isVisible) return null;

  return (
    <div className={`fixed top-1/2 left-0 -translate-y-1/2 z-40 transition-all duration-300 ${
      isClosing ? 'opacity-0 scale-95 -translate-x-full' : 'opacity-100 scale-100 translate-x-0'
    }`}>
      {/* Container with protruding effect */}
      <div className="flex flex-col gap-2 bg-white/90 backdrop-blur-sm shadow-xl rounded-r-xl pl-1 pr-3 py-2 border border-gray-100">
        {/* Close Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
          className="absolute -top-2 -right-2 z-10 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition-colors"
        >
          <FaTimes className="text-xs" />
        </button>

        {/* Degree Programs Icon */}
        <div
          onClick={handleClick}
          className="group relative -ml-6 hover:-ml-1 transition-all duration-300 cursor-pointer"
          title="Degree Programs - Click to Explore!"
        >
          <div className={`w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 via-pink-600 to-red-500 text-white shadow-md flex items-center justify-center transition-transform duration-200 group-hover:scale-105 ${
            isFlashing ? 'animate-pulse shadow-lg shadow-purple-500/50' : ''
          }`}>
            <FaGraduationCap className="w-6 h-6" />
          </div>
          
          {/* Floating elements for extra attention */}
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-ping"></div>
          <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-pink-400 rounded-full animate-ping" style={{animationDelay: '0.5s'}}></div>
        </div>

        {/* Additional small icons for visual appeal */}
        <div className="flex flex-col gap-1 ml-2">
          <div className="w-6 h-6 rounded-full bg-yellow-100 flex items-center justify-center">
            <FaStar className="text-yellow-500 text-xs" />
          </div>
          <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
            <FaTrophy className="text-green-500 text-xs" />
          </div>
          <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
            <FaRocket className="text-blue-500 text-xs" />
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes pulse {
          0%, 100% { 
            transform: scale(1);
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          }
          50% { 
            transform: scale(1.05);
            box-shadow: 0 10px 15px -3px rgba(147, 51, 234, 0.3);
          }
        }
        
        .animate-pulse {
          animation: pulse 1s infinite;
        }
      `}</style>
    </div>
  );
};

export default FloatingDegreeIcon;
